import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import api from "../../api/axiosInstance";

const ACCENT = "#E84610";
const TABS = [
  { key: "all", label: "All" },
  { key: "accepted", label: "Accepted" },
  { key: "pending", label: "Pending" },
  { key: "expired", label: "Expired" },
];
const STATUS_COLORS = {
  accepted: { bg: "#E6F4EA", fg: "#1E6B34" },
  pending: { bg: "#FFF3E0", fg: "#9A5B00" },
  expired: { bg: "#F5F0ED", fg: "#483C3B" },
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Invite Link Created" modal, per the Figma inspect values (text/secondary,
// input/label, MUI Large button).
const INVITE_TEXT_SECONDARY = "rgba(0, 0, 0, 0.6)";

// Table head row per Figma: 56px, Secondary Grays/25 fill, rounded top corners.
const HEAD_ROW_SX = {
  height: 56,
  "& th": {
    backgroundColor: "#EAE7E6",
    borderBottom: "1px solid #EAE7E6",
    py: 0,
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.87)",
  },
  "& th:first-of-type": { borderTopLeftRadius: "8px" },
  "& th:last-of-type": { borderTopRightRadius: "8px" },
};
const inviteLabel = (invite) => (invite.name || invite.email || "").toLowerCase();
const INVITE_CAPTION_SX = { fontSize: 12, lineHeight: "12px", letterSpacing: "0.15px", fontWeight: 400, color: INVITE_TEXT_SECONDARY };
const INVITE_BUTTON_SX = {
  height: 42,
  px: "22px",
  py: "8px",
  borderRadius: "8px",
  fontSize: 15,
  lineHeight: "26px",
  fontWeight: 500,
  letterSpacing: "0.46px",
  textTransform: "uppercase",
  boxSizing: "border-box",
};

const formatSent = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? "s" : ""} ago`;
};

const errorMessage = (err, fallback) => err.response?.data?.error || fallback;

const InviteFormFields = ({ values, setValues, showErrors }) => {
  const emailInvalid = showErrors && !EMAIL_RE.test(values.email.trim());
  const nameInvalid = showErrors && !values.name.trim();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      <TextField
        label="Full Name"
        required
        value={values.name}
        onChange={(e) => setValues({ ...values, name: e.target.value })}
        error={nameInvalid}
        helperText={nameInvalid ? "Full name is required" : undefined}
        fullWidth
      />
      <TextField
        label="Email"
        type="email"
        required
        value={values.email}
        onChange={(e) => setValues({ ...values, email: e.target.value })}
        error={emailInvalid}
        helperText={emailInvalid ? "Enter a valid email address" : undefined}
        fullWidth
      />
      <Typography variant="body2" color="text.secondary">
        Invitation expires in 30 days
      </Typography>
    </Box>
  );
};

const AdminInvites = () => {
  const [invites, setInvites] = useState([]);
  const [counts, setCounts] = useState({ all: 0, accepted: 0, pending: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("all");
  const [nameOrder, setNameOrder] = useState(null); // null = server order (newest first)
  const [snack, setSnack] = useState(null);

  const [modal, setModal] = useState(null); // "user" | "link" | null
  const [values, setValues] = useState({ name: "", email: "" });
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [createdLink, setCreatedLink] = useState(null);
  const [creatingLink, setCreatingLink] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/invites");
      setInvites(data.invites || []);
      setCounts(data.counts);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, "Failed to load invitations"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openInviteUser = () => {
    setValues({ name: "", email: "" });
    setShowErrors(false);
    setFormError(null);
    setModal("user");
  };
  const closeModal = () => setModal(null);

  // No form: the link invite is created straight away and shown.
  const createLink = async () => {
    setCreatingLink(true);
    try {
      const { data } = await api.post("/invites/link");
      setCreatedLink(data.link);
      setModal("link");
      load();
    } catch (err) {
      setSnack({ severity: "error", message: errorMessage(err, "Could not create an invite link") });
    } finally {
      setCreatingLink(false);
    }
  };

  const submit = async () => {
    const valid = EMAIL_RE.test(values.email.trim()) && values.name.trim();
    if (!valid) {
      setShowErrors(true);
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const { data } = await api.post("/invites", {
        name: values.name.trim(),
        email: values.email.trim(),
      });
      closeModal();
      setSnack({ severity: "success", message: `Invitation sent to ${data.email}` });
      load();
    } catch (err) {
      if (err.response?.status === 502) {
        // The record exists even though the email failed: close the form (a
        // second submit would hit "already open") and let the row's Resend retry.
        closeModal();
        setSnack({ severity: "error", message: errorMessage(err, "The email could not be sent") });
        load();
      } else {
        setFormError(errorMessage(err, "Something went wrong"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(createdLink);
    } catch {
      setSnack({ severity: "error", message: "Could not copy - select the link and copy it manually" });
    }
  };

  const act = async (invite, action) => {
    try {
      await api.post(`/invites/${invite.id}/${action}`);
      setSnack({
        severity: "success",
        message: action === "cancel" ? "Invitation cancelled" : `Invitation resent to ${invite.email}`,
      });
    } catch (err) {
      setSnack({ severity: "error", message: errorMessage(err, `Failed to ${action} invitation`) });
    }
    load();
  };

  const filtered = tab === "all" ? invites : invites.filter((i) => i.status === tab);
  const rows = nameOrder
    ? [...filtered].sort((a, b) => inviteLabel(a).localeCompare(inviteLabel(b)) * (nameOrder === "asc" ? 1 : -1))
    : filtered;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="displaySm" component="h1">Pending Invitations</Typography>
          <Typography variant="md" component="p" color="text.secondary">
            Invite people to the talent community and track their invitations
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" color="inherit" onClick={createLink} disabled={creatingLink}>
            Create Invite Link
          </Button>
          <Button
            variant="contained"
            onClick={openInviteUser}
            sx={{ backgroundColor: ACCENT, "&:hover": { backgroundColor: "#C93B0A" } }}
          >
            + Invite User
          </Button>
        </Box>
      </Box>

      <Paper elevation={1}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
          {TABS.map(({ key, label }) => (
            <Tab key={key} value={key} label={`${label} (${counts[key] ?? 0})`} />
          ))}
        </Tabs>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 3 }}>{error}</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={HEAD_ROW_SX}>
                  <TableCell>
                    <TableSortLabel
                      active={!!nameOrder}
                      direction={nameOrder || "asc"}
                      onClick={() => setNameOrder(nameOrder === "asc" ? "desc" : "asc")}
                      IconComponent={SwapVertIcon}
                      sx={{ "& .MuiTableSortLabel-icon": { opacity: 1, fontSize: 18, ml: 1, transform: "none" } }}
                    >
                      Invitation
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Invite Sent/ Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No invitations to show
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((invite) => {
                    const colors = STATUS_COLORS[invite.status] || STATUS_COLORS.expired;
                    return (
                      <TableRow key={invite.id} hover>
                        <TableCell>
                          <Typography variant="md" component="div" fontWeight={600}>{invite.name || invite.email}</Typography>
                          {invite.name && (
                            <Typography variant="sm" component="div" color="text.secondary">{invite.email}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                            sx={{ backgroundColor: colors.bg, color: colors.fg, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>{formatSent(invite.sentAt)}</TableCell>
                        <TableCell>
                          {invite.status === "pending" && (
                            <>
                              <Tooltip title="Resend invitation">
                                <IconButton aria-label="Resend invitation" onClick={() => act(invite, "resend")}>
                                  <RefreshIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel invitation">
                                <IconButton aria-label="Cancel invitation" onClick={() => act(invite, "cancel")}>
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {invite.status === "expired" && (
                            <Tooltip title="Resend by email (renews for 30 days)">
                              <IconButton aria-label="Resend by email" onClick={() => act(invite, "resend")}>
                                <MailOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={modal === "user"} onClose={closeModal} fullWidth maxWidth="xs">
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <InviteFormFields values={values} setValues={setValues} showErrors={showErrors} />
          {formError && <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={closeModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={submitting}
            sx={{ backgroundColor: ACCENT, "&:hover": { backgroundColor: "#C93B0A" } }}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modal === "link" && !!createdLink}
        onClose={closeModal}
        PaperProps={{
          sx: {
            width: 575,
            maxWidth: "calc(100% - 32px)",
            m: 2,
            p: 3,
            borderRadius: "8px",
            boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.3), 0px 2px 6px 2px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 30 }}>
            <Typography component="h2" sx={{ fontSize: 18, lineHeight: "30px", fontWeight: 700 }}>
              Invite Link Created
            </Typography>
            <IconButton edge="end" size="small" aria-label="Close dialog" onClick={closeModal} sx={{ color: "#483C3B" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: 16, lineHeight: "24px", fontWeight: 300, color: INVITE_TEXT_SECONDARY }}>
            Your magic link invitation is ready. Copy the link below and send it to the invitee to grant access.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography component="label" htmlFor="invite-link-field" sx={INVITE_CAPTION_SX}>
            Generated Invitation Link
          </Typography>
          <TextField
            id="invite-link-field"
            value={createdLink || ""}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": { backgroundColor: "#EAE7E6", borderRadius: "4px", pr: 2 },
              "& .MuiOutlinedInput-input": { py: "12px", pl: 2, height: 24, fontSize: 16, lineHeight: "24px" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.23)" },
            }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <LinkIcon sx={{ color: "#483C3B" }} />
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={INVITE_CAPTION_SX}>This invitation link expires in 30 days.</Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={closeModal}
            sx={{
              ...INVITE_BUTTON_SX,
              color: "#483C3B",
              borderColor: "#483C3B",
              backgroundColor: "#FFFFFF",
              "&:hover": { borderColor: "#483C3B", backgroundColor: "#FFFFFF" },
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={copyLink}
            sx={{ ...INVITE_BUTTON_SX, backgroundColor: ACCENT, "&:hover": { backgroundColor: "#C93B0A" } }}
          >
            Copy Link
          </Button>
        </Box>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default AdminInvites;
